import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Group, Loader, Modal, Stack, Text } from '@mantine/core'

type Check =
  | { kind: 'counting' }
  | { kind: 'clear' }
  | { kind: 'blocked'; count: number }
  | { kind: 'unknown'; message: string }

/**
 * Deleting a record that other records may point at.
 *
 * The count runs when the dialog opens rather than being kept on the row: it is
 * one query per deletion attempt instead of one per row on every render, and
 * the number is only ever wanted at the moment somebody is about to act on it.
 *
 * This is a guard against a mistake, not an enforced constraint. Firestore
 * rules cannot ask whether a query returns nothing, so nothing here stops a
 * determined admin, or a session created in the moment between the count and
 * the delete. It catches the case that actually happens, which is somebody not
 * realising anything referred to it.
 *
 * A failed count blocks too. Not knowing whether something is referenced is not
 * the same as knowing it is not, and the safe reading of silence is that the
 * deletion should wait.
 */
export default function ConfirmDelete({
  opened,
  onClose,
  /** What is being deleted, lower case: "circuit", "series". */
  what,
  name,
  /** What points at it, lower case and singular: "event", "session". */
  referenceNoun,
  countReferences,
  onDelete,
  /** Anything worth saying about what deletion costs, beyond the record. */
  note,
}: {
  opened: boolean
  onClose: () => void
  what: string
  name: string
  referenceNoun: string
  countReferences: () => Promise<number>
  onDelete: () => Promise<unknown>
  note?: string
}) {
  const [check, setCheck] = useState<Check>({ kind: 'counting' })
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  // Held in a ref because callers pass an inline arrow, which is a new function
  // on every render of the page behind the dialog. Depending on it directly
  // would re-run the count on every Firestore snapshot for as long as the
  // dialog is open.
  const count = useRef(countReferences)
  count.current = countReferences

  useEffect(() => {
    if (!opened) return
    let live = true
    setCheck({ kind: 'counting' })
    setFailed(null)
    count.current()
      .then((count) => {
        if (!live) return
        setCheck(count > 0 ? { kind: 'blocked', count } : { kind: 'clear' })
      })
      .catch((e: unknown) => {
        if (!live) return
        setCheck({ kind: 'unknown', message: e instanceof Error ? e.message : 'Check failed.' })
      })
    // Cancelled if the dialog closes first, so a slow count cannot arrive
    // afterwards and re-enable a button nobody is looking at.
    return () => {
      live = false
    }
  }, [opened])

  async function remove() {
    if (busy || check.kind !== 'clear') return
    setBusy(true)
    setFailed(null)
    try {
      await onDelete()
      onClose()
    } catch (err) {
      setFailed(err instanceof Error ? err.message : 'Could not delete.')
    } finally {
      setBusy(false)
    }
  }

  const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`

  return (
    <Modal opened={opened} onClose={() => !busy && onClose()} title={`Delete this ${what}?`}>
      <Stack gap="md">
        {failed && <Alert color="red">{failed}</Alert>}

        {check.kind === 'counting' && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              Checking whether anything uses it…
            </Text>
          </Group>
        )}

        {check.kind === 'blocked' && (
          <Alert color="yellow">
            <strong>{name}</strong> is used by {plural(check.count, referenceNoun)}. Deleting it
            would leave {check.count === 1 ? 'that one' : 'those'} pointing at nothing, so move{' '}
            {check.count === 1 ? 'it' : 'them'} to another {what} first.
          </Alert>
        )}

        {check.kind === 'unknown' && (
          <Alert color="red">
            Could not check what uses <strong>{name}</strong>: {check.message}. Not knowing is not
            the same as knowing nothing does, so this is left alone.
          </Alert>
        )}

        {check.kind === 'clear' && (
          <Text size="sm">
            No {referenceNoun} uses <strong>{name}</strong>, so it can go. {note} This cannot be
            undone.
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button color="red" loading={busy} disabled={check.kind !== 'clear'} onClick={() => void remove()}>
            Delete {what}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
